import { Box, styled } from '@mui/material';
import { type PointerEvent, useRef } from 'react';
import { useStore } from 'reactflow';
import {
  clampStepWidth,
  type WorkflowStepWidthLimits,
} from 'src/pages/Settings/workflows/utils/workflow-helpers';

export interface StepWidthGripProps {
  /** The width the card draws at now. */
  width: number;
  limits: WorkflowStepWidthLimits;
  /** Called with each new clamped width while dragging; not called when the width is unchanged. */
  onResize: (width: number) => void;
  /** Double-click: back to the type's default width. */
  onReset: () => void;
}

const Grip = styled(Box)(({ theme }) => ({
  position: 'absolute',
  // centred on the card's right border
  right: theme.spacing(-0.375),
  bottom: theme.spacing(1),
  width: theme.spacing(0.75),
  height: theme.spacing(3.5),
  borderRadius: theme.spacing(0.375),
  backgroundColor: theme.palette.custom.OV.sys.outline,
  opacity: 0.75,
  cursor: 'ew-resize',
  touchAction: 'none',
  zIndex: 6,
  '&:hover, &[data-dragging="true"]': {
    opacity: 1,
    backgroundColor: theme.palette.custom.OV.sys.primary,
  },
}));

interface Drag {
  pointerId: number;
  startX: number;
  startWidth: number;
  /** The last width reported, so a move inside the same 10px step reports nothing. */
  last: number;
}

const zoomSelector = (state: { transform: [number, number, number] }) => state.transform[2];

/**
 * The grip on a selected draft step's right edge. Dragging it changes the card's width, divided by
 * the canvas zoom so the edge follows the pointer; it carries `nodrag` so reactflow doesn't move
 * the step instead.
 */
export const StepWidthGrip = ({ width, limits, onResize, onReset }: StepWidthGripProps) => {
  const zoom = useStore(zoomSelector);
  const drag = useRef<Drag | null>(null);

  const endDrag = (event: PointerEvent<HTMLDivElement>) => {
    if (!drag.current || drag.current.pointerId !== event.pointerId) return;
    drag.current = null;
    event.currentTarget.dataset.dragging = 'false';
    if (event.currentTarget.hasPointerCapture?.(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  };

  return (
    <Grip
      className="nodrag nopan"
      role="separator"
      aria-orientation="vertical"
      aria-label="Resize step"
      aria-valuenow={width}
      aria-valuemin={limits.min}
      aria-valuemax={limits.max}
      title="Drag to resize. Double-click to reset."
      onPointerDown={(event) => {
        if (event.button !== 0) return;
        // keep the press from selecting, opening or dragging the step underneath
        event.preventDefault();
        event.stopPropagation();
        event.currentTarget.setPointerCapture?.(event.pointerId);
        event.currentTarget.dataset.dragging = 'true';
        drag.current = {
          pointerId: event.pointerId,
          startX: event.clientX,
          startWidth: width,
          last: width,
        };
      }}
      onPointerMove={(event) => {
        const current = drag.current;
        if (!current || current.pointerId !== event.pointerId) return;

        const next = clampStepWidth(
          current.startWidth + (event.clientX - current.startX) / (zoom || 1),
          limits,
        );
        if (next === current.last) return;
        current.last = next;
        onResize(next);
      }}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
      onClick={(event) => event.stopPropagation()}
      onDoubleClick={(event) => {
        event.stopPropagation();
        onReset();
      }}
    />
  );
};
